import { BounceButton } from '@/common/components/base/BounceButton';
import { css } from '@emotion/react';
import {
  colors,
  FlexColumn,
  FlexRow,
  fullSize,
  paddingHorizontal,
  relative,
} from '@toilet-paper-games/ui';
import { ChevronUp, Fullscreen, Menu, Minimize } from 'lucide-react';
import { action } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useControllerAppModel } from '../models/ControllerAppModel';

// Interface for props
interface ControllerTopBarProps {}

// Interface for ToggleBarButton props
interface ToggleBarButtonProps {
  hideBar: boolean;
  setHideBar: (value: boolean) => void;
}

// Style variables
const topBarStyles = {
  container: css(
    {
      zIndex: 100,
      height: 40,
      background: colors.componentHeaderBackground,
      color: 'white',
      transition: 'margin-top 0.2s',
    },
    relative(),
    paddingHorizontal(10),
  ),
  toggleButtonContainer: css({
    position: 'absolute',
    left: '50%',
    bottom: 0,
    transition: 'translate .2s',
  }),
  toggleButtonMask: css({
    '--r': '7.5px',
    lineHeight: 1.8,
    borderInline: 'var(--r) solid #0000',
    borderRadius: '0 0 calc(2*var(--r)) calc(2*var(--r))/var(--r)',
    mask: `radial-gradient(var(--r) at var(--r) 100%,#0000 98%,#000 101%)
      calc(-1*var(--r)) 0/100% var(--r) repeat-x,
      conic-gradient(#000 0 0) padding-box`,
    width: 60,
    height: 25,
    background: `color-mix(in srgb, ${colors.componentHeaderBackground} 75%, transparent)`,
  }),
  chevronIcon: (hideBar: boolean) =>
    css({
      rotate: hideBar ? 'x 180deg' : '0',
      transition: 'rotate 0.2s',
    }),
};

// Component for the toggle button that hides/shows the top bar
const ToggleBarButton = observer((props: ToggleBarButtonProps) => {
  const { hideBar, setHideBar } = props;

  return (
    <FlexColumn
      css={[
        topBarStyles.toggleButtonContainer,
        { translate: `-50% ${!hideBar ? '0' : 'calc(100% - 1px)'}` },
      ]}
    >
      <div css={[topBarStyles.toggleButtonMask, relative()]}>
        <BounceButton
          onClick={() => {
            setHideBar(!hideBar);
          }}
          css={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            translate: '-50% -50%',
            width: 35,
            height: 35,
          }}
        >
          <FlexColumn css={[fullSize]} center>
            <ChevronUp css={[topBarStyles.chevronIcon(hideBar)]} />
          </FlexColumn>
        </BounceButton>
      </div>
    </FlexColumn>
  );
});

// Component for the Settings button
const FullscreenButton = observer(() => {
  const appStore = useControllerAppModel();

  return (
    <BounceButton onClick={appStore.uiModel.toggleFullscreen}>
      {appStore.uiModel.isFullscreen ? <Minimize /> : <Fullscreen />}
    </BounceButton>
  );
});

// Component for the Menu button
const MenuButton = observer(() => {
  const appStore = useControllerAppModel();

  return (
    <BounceButton
      onClick={action(() => {
        appStore.uiModel.showMenuDialog = true;
      })}
    >
      <Menu />
    </BounceButton>
  );
});

const isIphone = /iPhone/.test(navigator.userAgent);

// Main ControllerTopBar component
export const ControllerTopBar = observer((props: ControllerTopBarProps) => {
  const [hideBar, setHideBar] = useState(false);

  return (
    <FlexRow
      css={[topBarStyles.container, { marginTop: hideBar ? -40 : 0 }]}
      justifyContent="space-between"
      alignItems="center"
    >
      <ToggleBarButton hideBar={hideBar} setHideBar={setHideBar} />
      <FlexRow alignItems="center">{!isIphone && <FullscreenButton />}</FlexRow>
      <FlexRow alignItems="center">
        <MenuButton />
      </FlexRow>
    </FlexRow>
  );
});
